import { useRef } from 'react';
import { LuPin } from 'react-icons/lu';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, useSheetClose } from '@/components/ui/sheet';
import { useDockState } from 'views/dock/dock-state';

import { OpportunityLink, useSubmitOpportunityForm } from '../useSubmitOpportunityForm';
import { OpportunityForm, OpportunityFormRef, OpportunityFormType } from './OpportunityForm';

type CreateOpportunitySheetProps = {
    householdGUID: string;
    defaultFormValues: Partial<OpportunityFormType>;
    link?: OpportunityLink;
    onClose: () => void;
};

export const CreateOpportunitySheet = (props: CreateOpportunitySheetProps) => (
    <Sheet defaultOpen onExitComplete={props.onClose}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" aria-describedby={undefined}>
            <CreateOpportunitySheetBody {...props} />
        </SheetContent>
    </Sheet>
);

const CreateOpportunitySheetBody = (props: CreateOpportunitySheetProps) => {
    const close = useSheetClose();
    const submitOpportunityForm = useSubmitOpportunityForm();
    const { addCreateOpportunityDockItem } = useDockState();
    const formRef = useRef<OpportunityFormRef>(null);

    // Move the in-progress form into the dock so the user can keep browsing; the dock item
    // carries the same household and suggestion link as this sheet.
    const handlePin = () => {
        addCreateOpportunityDockItem(props.householdGUID, formRef.current?.formMethods.getValues(), props.link);
        close();
    };

    return (
        <>
            <SheetHeader>
                <SheetTitle>
                    <div className="flex items-center gap-2">
                        <span className="text-accent text-lg font-semibold">Create opportunity</span>
                        <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Pin opportunity"
                            className="shrink-0"
                            onClick={handlePin}
                        >
                            <LuPin className="h-4 w-4" />
                        </Button>
                    </div>
                </SheetTitle>
            </SheetHeader>
            <div className="pt-4">
                <OpportunityForm
                    ref={formRef}
                    showProspectInput={false}
                    prospectGUID={props.householdGUID}
                    // The prospect control is hidden, yet the form output reads prospectOption.
                    defaultValues={{
                        ...props.defaultFormValues,
                        prospectOption: { value: props.householdGUID, label: '' },
                    }}
                    onCancel={close}
                    onSubmit={async (data) => {
                        if (await submitOpportunityForm.submit(data, props.link)) close();
                    }}
                />
            </div>
        </>
    );
};
